import React, { useState } from "react"
import { Typography, Divider, Row, Col, Tooltip, Collapse, InputNumber, Button, Input } from "antd"
import PropTypes from "prop-types"
import { useWatch } from "antd/es/form/Form"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import { InfoCircleOutlined, EditOutlined, ReloadOutlined, CheckOutlined } from "@ant-design/icons"
import { calculatePaymentBreakdown } from "utils/bookingCalculations"
import { computeDiscountAmount } from "utils/discountUtils"
import { colors } from "themes/variables"

import { isFailedPaymentStatus } from "ui/Booking/Details/utils"
import FlatRatePriceTooltip from "ui/FlatRatePriceTooltip"
import styles from "./BookingForm.module.css"

const { Text } = Typography

const PriceBreakdown = ({
  form,
  booking,
  isEdit,
  isPriceDecreased,
  hasDepositPayment,
  appliedPromos = [],
  isExperienceChanged = false,
  isOriginalDiscountRemoved = false,
  bookingSelections
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [localValue, setLocalValue] = useState(null)
  const [localReason, setLocalReason] = useState("")

  const paymentSummary = booking?.payment_summary ?? {}
  const { discount_amount, discount_code } = paymentSummary
  const { t } = useTranslation()
  const depositPercent = useWatch("depositPercent", form) || (booking?.deposit_percentage ? booking.deposit_percentage : "")
  const customDepositAmount = useWatch("customDepositAmount", form)
  const paymentOption = useWatch("paymentOption", form)
  const depositOnly = useWatch("depositOnly", form)
  const adjustedPrice = useWatch("adjustedPrice", form)
  const adjustmentReasonWatch = useWatch("adjustmentReason", form)
  // In edit mode the adjustment is read from the booking; fall back to it so the struck-through
  // adjusted display and reason render even before the watched form fields settle.
  const bookingSubtotalAdjusted = isEdit && !!paymentSummary.subtotal_adjusted && paymentSummary.adjusted_subtotal != null
  const isAdjusted = (adjustedPrice != null && adjustedPrice >= 0) || bookingSubtotalAdjusted
  const adjustmentReason = adjustmentReasonWatch ?? (isEdit ? paymentSummary.subtotal_adjustment_reason : null)

  const baseAmount = bookingSelections?.baseAmount ?? 0
  const addOnsTotal = bookingSelections?.addOnsTotal ?? 0
  const originalBaseAmount = bookingSelections?.originalBaseAmount ?? bookingSelections?.baseAmount ?? 0

  const recomputedExistingDiscount = paymentSummary?.discount_type && paymentSummary?.discount_value != null
    ? computeDiscountAmount(
        {
          value: Number(paymentSummary.discount_value),
          discount_type: paymentSummary.discount_type,
          apply_to_add_ons: paymentSummary?.applicability_rules?.apply_to_add_ons ?? paymentSummary?.apply_to_add_ons ?? false
        },
        baseAmount,
        addOnsTotal
      )
    : Math.max(0, Number(discount_amount || 0))
  // Two things end the booking's saved discount: swapping the experience, and the guide removing
  // the coupon here. Either way it arrives in appliedPromos if it survives, so falling back to
  // payment_summary would put the old code's row and its total back on screen — which is exactly
  // what made a removed coupon look like it was still applied.
  const effectiveEditDiscount =
    isEdit && !isExperienceChanged && !isOriginalDiscountRemoved && recomputedExistingDiscount > 0 && appliedPromos.length === 0
      ? recomputedExistingDiscount
      : 0

  const {
    percent,
    depositAmount,
    depositDueNow,
    balanceAmount,
    payment_history = [],
    refundDueNow
  } = calculatePaymentBreakdown({
    booking,
    bookingSelections,
    depositPercent,
    customDepositAmount,
    isPriceDecreased,
    paymentOption
  })

  const breakdown = bookingSelections?.breakdown ?? []
  const taxIndex = breakdown.findIndex((item) => item?.isTaxFee || item?.label?.toLowerCase().includes("tax"))
  const beforeTax = taxIndex >= 0 ? breakdown.slice(0, taxIndex) : breakdown
  const taxAndAfter = taxIndex >= 0 ? breakdown.slice(taxIndex) : []

  // Guest lines are everything before "Adjusted Price" in the breakdown
  const guestLines = beforeTax.filter((item) => item?.label !== "Adjusted Price")

  // Reused for both the generic `info` string and the clamped-to-$0 explanation, so a line can
  // carry either without the caller having to know the copy.
  const renderInfoIcon = (title) => (
    <Tooltip title={title}>
      <InfoCircleOutlined className="ml-xxs" style={{ color: "var(--color-primary)" }} />
    </Tooltip>
  )

  const renderBreakdownItem = (item) =>
    item?.children?.length > 0 ? (
      <Collapse
        key={`${item?.label}-${item?.amount}`}
        ghost
        style={{ marginTop: 8 }}
        items={[
          {
            key: "tax-fees",
            styles: {
              header: { padding: 0 },
              body: { padding: "4px 0 0 16px" }
            },
            label: (
              <Row justify="space-between">
                <Col>
                  <Text>{item?.label}</Text>
                </Col>
                <Col>
                  <Text>{item?.amount}</Text>
                </Col>
              </Row>
            ),
            children: item.children.map((child) => (
              <Row key={`${child?.label}-${child?.amount}`} justify="space-between" style={{ marginTop: 4 }}>
                <Col>
                  <Text type="secondary">{child?.label}</Text>
                </Col>
                <Col>
                  <Text type="secondary">{child?.amount}</Text>
                </Col>
              </Row>
            ))
          }
        ]}
      />
    ) : (
      <Row key={`${item?.label}-${item?.amount}`} justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text>{item?.label}</Text>
          {item?.pricingDetails && <FlatRatePriceTooltip {...item.pricingDetails} />}
          {item?.isZeroedByAdjustment
            ? renderInfoIcon(t("booking.warnings.price_zeroed_by_adjustment"))
            : !item?.pricingDetails && item?.info && renderInfoIcon(item.info)}
        </Col>
        <Col>
          <Text>{item?.amount}</Text>
        </Col>
      </Row>
    )

  const totalAfterDiscounts = Math.max(0, bookingSelections?.amount ?? 0)
  const hasAnyDiscount = appliedPromos.length > 0 || effectiveEditDiscount > 0
  const showDepositDueNow = (isEdit && hasDepositPayment && !isPriceDecreased) || (!isEdit && depositOnly)
  const showRemainingBalance =
    (showDepositDueNow && balanceAmount > 0) || (isEdit && !hasDepositPayment && refundDueNow <= 0)

  const handleEditClick = () => {
    setLocalValue(isAdjusted ? adjustedPrice : originalBaseAmount)
    setLocalReason(adjustmentReason ?? "")
    setIsEditing(true)
  }

  const handleApply = () => {
    form.setFieldsValue({ adjustedPrice: localValue, adjustmentReason: localReason?.trim() || null })
    setIsEditing(false)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleReset = () => {
    form.setFieldsValue({ adjustedPrice: null, adjustmentReason: null })
    setLocalReason("")
    setIsEditing(false)
  }

  return (
    <div>
      {guestLines.map(renderBreakdownItem)}

      {/* Subtotal row with inline adjust */}
      {guestLines.length > 0 && (
        <>
          <Row justify="space-between" align="middle" style={{ marginTop: 8 }}>
            <Col>
              <Text style={{ fontWeight: 500 }}>{t("booking.labels.subtotal", "Subtotal")}</Text>
              {!isEdit && !isEditing && (
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={handleEditClick}
                  style={{ padding: "0 4px", color: "var(--color-primary)", fontSize: 13 }}
                >
                  {t("global.adjust", "Adjust")}
                </Button>
              )}
              {!isEdit && !isEditing && isAdjusted && (
                <Button
                  type="link"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  style={{ padding: "0 4px", color: "var(--color-text-secondary)", fontSize: 13 }}
                >
                  {t("global.reset", "Reset")}
                </Button>
              )}
            </Col>
            <Col>
              {isAdjusted ? (
                <span>
                  <Text delete type="secondary" style={{ marginRight: 6 }}>
                    {numberToCurrency(originalBaseAmount)}
                  </Text>
                  <Text strong style={{ color: "var(--color-primary)", marginRight: 4 }}>
                    {numberToCurrency(baseAmount)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    ({t("global.adjusted", "adjusted")})
                  </Text>
                </span>
              ) : (
                <Text>{numberToCurrency(originalBaseAmount)}</Text>
              )}
            </Col>
          </Row>

          {/* Reason is shown in edit mode too (read-only) so the carried-over adjustment stays legible. */}
          {!isEditing && isAdjusted && adjustmentReason && (
            <Row style={{ marginTop: 2 }}>
              <Col>
                <Text type="secondary" style={{ fontSize: 12, fontStyle: "italic" }}>
                  {t("booking.labels.reason", "Reason")}: {adjustmentReason}
                </Text>
              </Col>
            </Row>
          )}

          {!isEdit && isEditing && (
            <div className={styles.adjustPricePanel}>
              <Text className={styles.adjustPricePanelTitle}>
                {t("booking.adjusted_price_label", "Adjusted Price (Subtotal)")}
              </Text>
              <InputNumber
                value={localValue}
                onChange={setLocalValue}
                min={0}
                precision={2}
                prefix="$"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                autoFocus
                // Numeric-only: block letters/"e"/symbols on keypress and strip them on paste.
                onKeyDown={(e) => {
                  const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End", "Enter"]
                  if (allowedKeys.includes(e.key) || e.metaKey || e.ctrlKey) return
                  if (!/^[0-9.]$/.test(e.key)) e.preventDefault()
                }}
                parser={(value) => (value ? value.replace(/[^\d.]/g, "") : "")}
              />
              <Input.TextArea
                value={localReason}
                onChange={(e) => setLocalReason(e.target.value)}
                placeholder={t("booking.adjustment_reason_placeholder", "Reason for adjustment (optional)")}
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={255}
                style={{ marginTop: 8 }}
              />
              <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: "block" }}>
                {t("booking.adjust_price_hint", "Fees are automatically recalculated based on adjusted price")}
              </Text>
              <Row justify="end" gutter={8} style={{ marginTop: 12 }}>
                <Col flex="none">
                  <Button size="large" onClick={handleCancel}>
                    {t("global.cancel")}
                  </Button>
                </Col>
                <Col flex="none">
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    size="large"
                    onClick={handleApply}
                    disabled={localValue == null}
                  >
                    {t("global.apply", "Apply")}
                  </Button>
                </Col>
              </Row>
            </div>
          )}
        </>
      )}

      {addOnsTotal > 0 && (
        <Row justify="space-between" style={{ marginTop: 8 }}>
          <Col>
            <Text>{t("booking.add_ons.title", { defaultValue: "Add-ons" })}</Text>
          </Col>
          <Col>
            <Text>{numberToCurrency(addOnsTotal)}</Text>
          </Col>
        </Row>
      )}

      {/* Discounts are applied to the pre-tax base, so they render before Tax & Fees. */}
      {effectiveEditDiscount > 0 && (
        <Row justify="space-between" style={{ marginTop: 8 }}>
          <Col>
            <Text type="danger">{discount_code ? `Discount (${discount_code})` : "Discount"}</Text>
          </Col>
          <Col>
            <Text type="danger">-{numberToCurrency(effectiveEditDiscount)}</Text>
          </Col>
        </Row>
      )}

      {appliedPromos.map((promo) => (
        <Row key={promo.code} justify="space-between" style={{ marginTop: 8 }}>
          <Col>
            <Text type="secondary">{promo.type === "voucher" ? `Gift Card (${promo.code})` : `${promo.name} (${promo.code})`}</Text>
          </Col>
          <Col>
            <Text style={{ color: "var(--color-primary)" }}>
              -{numberToCurrency(computeDiscountAmount(promo, baseAmount, addOnsTotal))}
            </Text>
          </Col>
        </Row>
      ))}

      {taxAndAfter.map(renderBreakdownItem)}

      <Divider style={{ margin: "16px 0" }} />

      <Row justify="space-between" style={{ marginTop: hasAnyDiscount ? 8 : 0 }}>
        <Col>
          <Text strong>{hasAnyDiscount ? t("booking.labels.total") + " (after savings)" : t("booking.labels.total")}</Text>
        </Col>
        <Col>
          <Text strong>{numberToCurrency(totalAfterDiscounts)}</Text>
        </Col>
      </Row>
      {percent > 0 && (
        <>
          {isEdit &&
            payment_history?.map((history) => {
              const { type, method, last4, amount, id, status } = history || {}
              const isRefund = type?.toLowerCase() === "refund"
              // A failed row is money that never moved, and the breakdown below already ignores it.
              // Without the marker a declined refund reads as cash the guest got back, which makes
              // the balance look wrong rather than explaining itself.
              const isFailed = isFailedPaymentStatus(status)
              const key = id || `${type}-${method}-${last4}-${amount}`
              return (
                <Row key={key} justify="space-between" style={{ marginTop: 8 }}>
                  <Col>
                    <Text>
                      {type || ""} ({method || ""}
                      {last4 && ` **** ${last4}`})
                      {isFailed && ` — ${t("booking.reservation_detail.payment_failed")}`}
                    </Text>
                  </Col>
                  <Col>
                    <Text
                      delete={isFailed}
                      color={isFailed || isRefund ? colors.error : colors.textPrimary}
                    >
                      {amount ? numberToCurrency(amount) : ""}
                    </Text>
                  </Col>
                </Row>
              )
            })}
          {showDepositDueNow && (
            <Row justify="space-between" style={{ marginTop: 8 }}>
              <Col>
                <Text>{isEdit ? t("booking.labels.deposit_due_now") : depositOnly && t("booking.labels.pay_now_deposit")}</Text>
              </Col>
              <Col>
                <Text>{numberToCurrency(isEdit ? depositDueNow : depositOnly && depositAmount)}</Text>
              </Col>
            </Row>
          )}
          {showRemainingBalance && (
            <Row justify="space-between" style={{ marginTop: 8 }}>
              <Col>
                <Text strong>{t("booking.labels.remaining_balance")}</Text>
              </Col>
              <Col>
                <Text strong>{numberToCurrency(balanceAmount)}</Text>
              </Col>
            </Row>
          )}
        </>
      )}
      {isEdit && refundDueNow > 0 && (
        <Row justify="space-between" style={{ marginTop: 8 }}>
          <Col>
            <Text strong>{t("booking.labels.refund_due")}</Text>
          </Col>
          <Col>
            <Text strong>{numberToCurrency(refundDueNow)}</Text>
          </Col>
        </Row>
      )}
    </div>
  )
}

PriceBreakdown.propTypes = {
  form: PropTypes.object.isRequired,
  booking: PropTypes.object.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isPriceDecreased: PropTypes.bool.isRequired,
  hasDepositPayment: PropTypes.bool.isRequired,
  appliedPromos: PropTypes.array,
  isExperienceChanged: PropTypes.bool,
  isOriginalDiscountRemoved: PropTypes.bool,
  bookingSelections: PropTypes.object
}

export default PriceBreakdown
